import { Column } from '@components/LinearLayout';
import {
  CustomerItemRoot,
  EventCustomerTitle,
  EventCustomersSectionRoot,
} from '@pages/EventHome/components/EventCustomersSection/styles';
import { useEffect, useMemo, useState } from 'react';
import {
  FooterCard,
  HeaderContainer,
  PriceText,
  TicketsItemsWrapper,
  Title,
  TopBodyRoot,
  Underline,
  UnderlineContainer,
} from './styles';

import Button from '@components/Button';
import Divider from '@components/Divider';
import { adminEventsRouteFormat } from '@constants/paths';
import { getEventDetails } from '@store/slices/eventSlice';
import { fetchTicketsByPageType } from '@utils/ticketUtils';
import { useMobileWidth } from '@utils/hooks';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';

interface TicketItemProps {
  item: {
    ticketName: string;
    price: string;
  };
}

const TicketItem = ({ item }: TicketItemProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMobileWidth();
  const sluggedEvent = useSelector(getEventDetails)?.[0];
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState<boolean>(false);

  return (
    <article>
      <CustomerItemRoot
        onMouseOver={() => {
          setIsHovered(true);
        }}
        onMouseOut={() => {
          setIsHovered(false);
        }}
        onClick={() => {
          if (isMobile) return;
          navigate(adminEventsRouteFormat(sluggedEvent?.slug, ''));
        }}
        $isHovered={isHovered}
        $borderRadius="1.2rem"
        $width={isMobile ? '100%' : '33rem'}
      >
        <TopBodyRoot>
          <HeaderContainer>
            <Title>{item.ticketName}</Title>

            <Divider
              width="16.3rem"
              height="0.3rem"
              backgroundColor={
                sluggedEvent?.design?.themeColor || theme.color.primary
              }
            />

            <PriceText color={sluggedEvent?.design?.themeColor}>
              {item.price || '\u00A0'}
            </PriceText>
          </HeaderContainer>
        </TopBodyRoot>

        <FooterCard>
          <Button
            title={t('book-now')}
            backgroundColor={sluggedEvent?.design?.themeColor}
            hoverBackgroundColor={theme.color.mobilityGrey}
            textColor={theme.color.white}
            borderRadius={theme.borderRadius.radius10}
            padding={isMobile ? '1.5rem 2.5rem' : '1.1rem 4.9rem'}
            fontSize={isMobile ? theme.fontSize.size18 : theme.fontSize.size20}
            width={'100%'}
            onClick={() => {
              window.location.href = `${window.location.origin}/events/${sluggedEvent?.slug}${t('book-now-url')}`;
            }}
          />
        </FooterCard>
      </CustomerItemRoot>
    </article>
  );
};

const EventTicketsSection = () => {
  const { t } = useTranslation();
  const sluggedEvent = useSelector(getEventDetails)?.[0];
  const isMobile = useMobileWidth();
  const [tickets, setTickets] = useState<any[]>([]);

  useEffect(() => {
    if (sluggedEvent?.slug) {
      fetchTicketsByPageType(sluggedEvent.slug, 'home')
        .then(ticketsData => {
          // Convert tickets to the format expected by the component
          const ticketItems = ticketsData.map(ticket => ({
            ticketName: ticket.name,
            price: ticket.price ? `€ ${ticket.price}` : '',
          }));

          // Add "Further Event Passes" as the last item
          ticketItems.push({
            ticketName: t('further-event-passes'),
            price: '',
          });

          setTickets(ticketItems);
        })
        .catch(err => {
          console.error('error = ', err);
          // Fallback to default tickets if API fails
          setTickets([
            {
              ticketName: t('further-event-passes'),
              price: '',
            },
          ]);
        });
    }
  }, [sluggedEvent, t]);

  const eventTicketsItems = useMemo(() => tickets, [tickets]);

  const realTickets = useMemo(() => {
    return eventTicketsItems.filter(
      it =>
        it.ticketName !== t('further-event-passes') &&
        (it?.price ?? '').trim() !== '' &&
        it.ticketName
    );
  }, [eventTicketsItems, t]);
  const hasRealTickets = realTickets.length > 0;

  if (!hasRealTickets) return null;

  return (
    <section>
      <EventCustomersSectionRoot>
        <Column $gap="3rem">
          <Column $gap="1.7rem">
            <EventCustomerTitle>{t('register-now-v2')}</EventCustomerTitle>
            <UnderlineContainer>
              <Underline color={sluggedEvent?.design?.themeColor} />
            </UnderlineContainer>
          </Column>
        </Column>
        <TicketsItemsWrapper>
          {realTickets?.map(item => (
            <TicketItem item={item} key={item.ticketName} />
          ))}
        </TicketsItemsWrapper>
      </EventCustomersSectionRoot>
    </section>
  );
};

export default EventTicketsSection;
